import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { startCheckout, fetchCurrentPrice, type CurrentPrice } from "@/lib/billing";
import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";
import { AuthShell } from "./AuthShell";

/**
 * Trial-over gate. Self-serve signups get the full desk free for one hour; when the hour is
 * up they land here. Polls /api/me so the moment the account is activated (payment or a desk
 * admin flipping it), it advances to ops automatically.
 */
export function PendingScreen() {
  const { me, refreshMe, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState<CurrentPrice | null>(null);
  const [resent, setResent] = useState<"idle" | "busy" | "sent" | "failed">("idle");
  useEffect(() => {
    const id = setInterval(() => void refreshMe(), 15_000);
    // Checkout happens in another tab — clicking back here must re-check instantly, so a
    // fresh payer sees the desk open, not a stale "trial over" screen.
    const onFocus = () => void refreshMe();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    let alive = true;
    void fetchCurrentPrice().then((p) => { if (alive) setPrice(p); });
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshMe]);

  async function claim() {
    setBusy(true);
    const ok = await startCheckout();
    if (!ok) window.location.href = "mailto:info@earlywire.io?subject=Founding%20seat";
    setBusy(false);
  }

  async function resend() {
    setResent("busy");
    try {
      const token = await getIdToken();
      const r = await fetch(`${config.restUrl}/api/me/resend-confirmation`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = (await r.json().catch(() => ({}))) as { result?: string };
      setResent(j.result === "sent" || j.result === "throttled" ? "sent" : "failed");
    } catch {
      setResent("failed");
    }
  }

  const suspended = me?.reason === "suspended";
  const blocked = me?.reason === "blocked_email";
  const unconfirmed = me?.reason === "email_unconfirmed";

  if (unconfirmed) {
    return (
      <AuthShell title="CONFIRM YOUR EMAIL" subtitle="One click between you and the live desk">
        <p className="auth-copy">
          We sent a confirmation link to <b>{me?.contactEmail ?? me?.email}</b>. Click it and this
          screen unlocks your free hour automatically — the clock only starts once you're on the desk.
        </p>
        <p className="auth-copy" style={{ color: "var(--ivory-d)" }}>
          Nothing arriving? Check spam, or resend (limited to one per minute).
        </p>
        <button className="auth-btn primary" disabled={resent === "busy"} onClick={() => void resend()}>
          {resent === "busy" ? "SENDING…" : resent === "sent" ? "SENT — CHECK YOUR INBOX" : resent === "failed" ? "SEND FAILED — TRY AGAIN" : "RESEND CONFIRMATION EMAIL"}
        </button>
        <button className="auth-btn" onClick={() => void signOut()}>
          USE A DIFFERENT EMAIL
        </button>
      </AuthShell>
    );
  }

  if (blocked) {
    return (
      <AuthShell title="USE A REAL EMAIL" subtitle="Disposable addresses don't get a trial">
        <p className="auth-copy">
          <b>{me?.email}</b> looks like a temporary/disposable address. The free hour is for real
          operators — sign up again with your work email and you're on the desk in 60 seconds.
        </p>
        <button className="auth-btn primary" onClick={() => void signOut()}>
          SIGN UP WITH A REAL EMAIL
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={suspended ? "ACCOUNT SUSPENDED" : "YOUR FREE HOUR IS UP"}
      subtitle={suspended ? "Contact the desk" : "The desk keeps running — your seat is one step away"}
    >
      {suspended ? (
        <p className="auth-copy">
          Access for <b>{me?.email}</b> is suspended. If you think this is a mistake, mail{" "}
          <a href="mailto:info@earlywire.io" style={{ color: "var(--green)" }}>info@earlywire.io</a>.
        </p>
      ) : (
        <>
          <p className="auth-copy">
            You've seen the live desk with <b>{me?.email}</b> — the feed, the scores, the map.
            Signals kept firing after your hour ended; you just can't see them right now.
          </p>
          <p className="auth-copy">
            <b>Current rate: ${price?.usd ?? 149}/month, locked for as long as you stay</b>
            {price?.listUsd ? ` (list $${price.listUsd})` : ""}.
            {price?.seatsRemaining != null && <> Only <b>{price.seatsRemaining} seats left</b> at this rate — the ladder steps up as cohorts fill.</>}{" "}
            Your seat reopens the moment checkout completes.
          </p>
          <button className="auth-btn primary" disabled={busy} onClick={() => void claim()}>
            {busy ? "OPENING CHECKOUT…" : `CLAIM YOUR SEAT — $${price?.usd ?? 149}/MO`}
          </button>
        </>
      )}
      <button className="auth-btn" onClick={() => void signOut()}>
        SIGN OUT
      </button>
    </AuthShell>
  );
}
